import { FC, useState, useContext } from "react";
import { css, cx } from "@emotion/css";
import {
  Colors,
  Box,
  Icons,
  Chip,
  TextField,
  Typography,
  ButtonGroup,
  Tooltip,
} from "@analyticsMD/quil";
import { Button } from "@mui/material";
import qCustomColors from "../../colors";
import APIContext from "../../contexts/APIContext";
import { useMutation, useQueryClient } from "react-query";
import {
  postPatientInsightsQuoteScore,
  patchPatientInsightsQuoteScore,
} from "../../api/endpoints";
import PatientInsightsQuoteScore from "../../types/PatientInsightsQuoteScore";
import PatientInsightsQuote from "../../types/PatientInsightsQuote";
import { ActionTrack } from "../../types/Analytics";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import { deepPurple, grey } from "@mui/material/colors";

const styles = {
  insightItemQuoteContainer: css`
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  `,
  insightItemQuoteContainerFocus: css`
    margin-left: 0.6rem;
    margin-right: 0.6rem;
  `,
  insightItemQuoteFocus: css`
    background-color: rgba(0, 0, 0, 0.04);
    border-radius: 0.625rem;
    margin-left: -0.6rem;
    margin-right: -0.6rem;
  `,
  insightItemContainer: css`
    display: flex;
    justify-content: space-between;
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  `,
  insightItemFeedbackContainer: css`
    display: flex;
    justify-content: end;
  `,
  insightItemFeedbackIcons: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.2rem;
  `,
  thumbsUpIcon: (isPositiveFeedback: boolean) => css`
    color: ${isPositiveFeedback ? qCustomColors.successLight : grey[600]};
  `,
  thumbsDownIcon: (isPositiveFeedback: boolean) => css`
    color: ${isPositiveFeedback === false
      ? qCustomColors.warningDark
      : grey[600]};
  `,
  feedbackTextField: css`
    background-color: transparent;
    margin-top: 0.8rem !important;
    margin-bottom: 0.8rem !important;
    padding-left: 0 !important;
    width: 85%;

    & .MuiInputBase-root {
      background-color: ${Colors.white};
    }

    & .MuiInputBase-input {
      padding-top: 0.15rem;
      padding-bottom: 1rem;
      padding-left: 0.1rem;
      font-size: 0.875rem;
    }
  `,
  feedbackContainer: css`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin-top: 0.8rem;
  `,
  quoteText: css`
    list-style: none;
    padding-right: 0.5rem;
    padding-bottom: 0.5rem;
    font-size: 0.9rem;
  `,
  chipReason: (isSelected: boolean) => css`
    border-radius: 1.125rem !important;
    border: 1px solid ${deepPurple[600]} !important;
    background-color: ${isSelected
      ? qCustomColors.purpleLight
      : "transparent"} !important;
    .MuiChip-label {
      font-weight: 500;
      color: ${deepPurple[600]};
    }
  `,
  thumbsFeedbackIcon: {
    width: "1rem",
    height: "1rem",
  },
  feedbackInfoText: {
    fontSize: "0.75rem",
    color: grey[600],
    paddingLeft: "0.2rem",
  },
};

const sxStyles = {
  submitButton: {
    textTransform: "none",
    alignSelf: "flex-end",
    borderRadius: "0.4rem !important",
  },
  cancelButton: {
    marginRight: "0.5rem",
    padding: "0.375rem 1rem",
  },
  submitButtonText: { fontSize: "0.8rem" },
  insightTypeChip: {
    backgroundColor: qCustomColors.purpleLight2,
    color: deepPurple[800],
    fontSize: "0.7rem",
    marginBottom: "0.3rem",
    "& .MuiChip-icon": {
      color: deepPurple[600],
    },
  },
};

interface InsightItemQuoteProps {
  insightNoteID: number;
  score?: PatientInsightsQuoteScore;
  quote: PatientInsightsQuote;
  setShowToast: (showToast: boolean) => void;
  setShowErrorToast: (showToast: boolean) => void;
}

const InsightItemQuote: FC<InsightItemQuoteProps> = ({
  insightNoteID,
  score,
  quote,
  setShowToast,
  setShowErrorToast,
}) => {
  const [isPositiveFeedback, setIsPositiveFeedback] = useState<boolean | null>(
    score?.is_positive_feedback
  );

  const [quoteScore, setQuoteScore] =
    useState<PatientInsightsQuoteScore | null>(score);

  const [showFeedbackSection, setShowFeedbackSection] =
    useState<boolean>(false);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [otherReasonFreetext, setOtherReasonFreetext] = useState<string>(null);
  const { configurationQuery, analytics, patientDataReadOnly } =
    useContext(APIContext);
  const configuration = configurationQuery?.data
    ? configurationQuery?.data?.[0]
    : {};

  const queryClient = useQueryClient();
  const postMutation = useMutation(postPatientInsightsQuoteScore, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["patientinsights"] });
      setQuoteScore(data);
      setShowToast(true);
    },
    onError: () => {
      setShowErrorToast(true);
      resetFeedbackSection(true);
    },
  });

  const patchMutation = useMutation(patchPatientInsightsQuoteScore, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["patientinsights"] });
      if (data.is_positive_feedback === true) {
        setShowToast(true);
      }
    },
    onError: () => {
      setShowErrorToast(true);
      resetFeedbackSection(true);
    },
  });

  const resetFeedbackSection = (resetPositiveFeedback = false) => {
    setShowFeedbackSection(false);
    setSelectedReasons([]);
    setOtherReasonFreetext(null);

    if (resetPositiveFeedback) {
      setIsPositiveFeedback(null);
    }
  };

  const handleThumbsUpClick = () => {
    if (patientDataReadOnly) return;
    const newPositiveFeedback = isPositiveFeedback ? null : true;
    setIsPositiveFeedback(newPositiveFeedback);

    if (newPositiveFeedback !== null && !quoteScore) {
      postMutation.mutate({
        is_positive_feedback: newPositiveFeedback,
        insight_note: insightNoteID,
        quote_scored: quote.id,
      });
    } else if (quoteScore) {
      patchMutation.mutate({
        id: quoteScore.id,
        is_positive_feedback: newPositiveFeedback,
      });
    }

    analytics?.track(
      newPositiveFeedback
        ? ActionTrack.INSIGHTS_POSITIVE_FEEDBACK_RECEIVED
        : ActionTrack.INSIGHTS_FEEDBACK_REMOVED,
      {
        insight_note_id: insightNoteID,
        quote_id: quote.id,
        quote: quote.quote,
      }
    );

    resetFeedbackSection();
  };

  const handleThumbsDownClick = () => {
    if (patientDataReadOnly) return;
    const newPositiveFeedback = isPositiveFeedback === false ? null : false;
    setIsPositiveFeedback(newPositiveFeedback);

    if (newPositiveFeedback !== null) {
      setShowFeedbackSection(true);
    } else {
      if (quoteScore) {
        patchMutation.mutate({
          id: quoteScore.id,
          is_positive_feedback: null,
        });
      }

      analytics?.track(ActionTrack.INSIGHTS_FEEDBACK_REMOVED, {
        insight_note_id: insightNoteID,
        quote_id: quote.id,
        quote: quote.quote,
      });

      resetFeedbackSection();
    }
  };

  const renderFeedbackSection = () => {
    const reasons = configuration?.negative_insight_feedback_reasons;
    const handleReasonClick = (reason: string) => {
      if (patientDataReadOnly) return;
      setSelectedReasons((prevReasons) =>
        prevReasons.includes(reason)
          ? prevReasons.filter((r) => r !== reason)
          : [...prevReasons, reason]
      );
    };
    const otherReason = "Other";
    const otherReasonSelected = (
      <Box sx={{ display: "flex" }}>
        <span>{otherReason}</span>
        <Icons.CheckIcon
          className={css`
            padding-left: 0.25rem;
          `}
        />
      </Box>
    );

    const handleSubmit = () => {
      if (patientDataReadOnly) return;
      if (quoteScore) {
        patchMutation.mutate({
          id: quoteScore.id,
          is_positive_feedback: false,
          reasons: selectedReasons,
          reason_free_text: otherReasonFreetext,
        });
      } else {
        postMutation.mutate({
          is_positive_feedback: false,
          insight_note: insightNoteID,
          quote_scored: quote.id,
          reasons: selectedReasons,
          reason_free_text: otherReasonFreetext,
        });
      }
      analytics?.track(ActionTrack.INSIGHTS_NEGATIVE_FEEDBACK_RECEIVED, {
        insight_note_id: insightNoteID,
        quote_id: quote.id,
        quote: quote.quote,
        reasons: selectedReasons,
        reason_free_text: otherReasonFreetext,
      });

      setShowToast(true);
      resetFeedbackSection();
    };

    const handleCancel = () => {
      resetFeedbackSection(true);
    };

    const disableSubmit =
      selectedReasons.length === 0 ||
      (selectedReasons.includes(otherReason) && !otherReasonFreetext);

    return (
      <Box className={styles.feedbackContainer}>
        <Box sx={{ paddingBottom: "0.5rem" }}>
          <span>Why wasn’t this useful?</span>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {reasons.map((r) => {
            const isSelected = selectedReasons.includes(r.reason);
            const labelReason = isSelected ? (
              <Box sx={{ display: "flex" }}>
                <span>{r.reason}</span>
                <Icons.CheckIcon
                  className={css`
                    padding-left: 0.25rem;
                  `}
                />
              </Box>
            ) : (
              r.reason
            );
            return (
              <Chip
                key={r.reason}
                label={labelReason}
                onClick={() => handleReasonClick(r.reason)}
                variant={isSelected ? "filled" : "outlined"}
                className={styles.chipReason(isSelected)}
              />
            );
          })}
          <Chip
            label={
              selectedReasons.includes(otherReason)
                ? otherReasonSelected
                : otherReason
            }
            onClick={() => handleReasonClick(otherReason)}
            variant={
              selectedReasons.includes(otherReason) ? "filled" : "outlined"
            }
            className={styles.chipReason(selectedReasons.includes(otherReason))}
          />
        </Box>
        {selectedReasons.includes(otherReason) && (
          <TextField
            placeholder="Add a reason"
            value={otherReasonFreetext}
            multiline
            maxRows={4}
            className={styles.feedbackTextField}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setOtherReasonFreetext(event.target.value);
            }}
          />
        )}
        <ButtonGroup sx={{ justifyContent: "flex-end" }}>
          <Button
            variant="text"
            size="small"
            sx={{ ...sxStyles.submitButton, ...sxStyles.cancelButton }}
            onClick={handleCancel}
          >
            <Typography variant="body1" sx={sxStyles.submitButtonText}>
              Cancel
            </Typography>
          </Button>
          <Button
            variant="contained"
            sx={sxStyles.submitButton}
            onClick={handleSubmit}
            disabled={disableSubmit}
          >
            <Typography variant="body1" sx={sxStyles.submitButtonText}>
              Submit
            </Typography>
          </Button>
        </ButtonGroup>
      </Box>
    );
  };

  return (
    <Box className={showFeedbackSection ? styles.insightItemQuoteFocus : null}>
      <Box
        key={quote.id}
        className={
          showFeedbackSection
            ? cx(
                styles.insightItemQuoteContainer,
                styles.insightItemQuoteContainerFocus
              )
            : styles.insightItemQuoteContainer
        }
      >
        <Box className={styles.insightItemContainer}>
          <li className={styles.quoteText}>
            &quot;{quote.quote}&quot;&nbsp;{quote.suffix}
          </li>
          <Chip
            label={quote.insight_type}
            size="small"
            icon={<PendingActionsIcon />}
            sx={sxStyles.insightTypeChip}
          />
          <Box className={styles.insightItemFeedbackContainer}>
            <Tooltip title="Was this useful?" placement="left" arrow={false}>
              <Box className={styles.insightItemFeedbackIcons}>
                <ThumbUpOffAltIcon
                  className={styles.thumbsUpIcon(isPositiveFeedback)}
                  onClick={handleThumbsUpClick}
                  aria-selected={isPositiveFeedback}
                  sx={{
                    ...styles.thumbsFeedbackIcon,
                    ...(patientDataReadOnly
                      ? { opacity: 0.4, pointerEvents: "none" }
                      : {}),
                  }}
                />
                <ThumbDownOffAltIcon
                  className={styles.thumbsDownIcon(isPositiveFeedback)}
                  onClick={handleThumbsDownClick}
                  aria-selected={!isPositiveFeedback}
                  sx={{
                    ...styles.thumbsFeedbackIcon,
                    ...(patientDataReadOnly
                      ? { opacity: 0.4, pointerEvents: "none" }
                      : {}),
                  }}
                />
                <Typography variant="body2" sx={styles.feedbackInfoText}>
                  Leave feedback
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        </Box>
        {showFeedbackSection && renderFeedbackSection()}
      </Box>
    </Box>
  );
};
export default InsightItemQuote;
